import { useId } from 'react';
import { createPortal } from 'react-dom';
import { QuantityStepper } from './QuantityStepper';
import { RipButton } from './RipButton';
import { formatPrice } from '../../../../modules/currency';

export interface BottomActionProps {
  quantity: number;
  onQuantityChange: (next: number) => void;
  onBuy: () => void;
  /** Per-unit price (number like 25 or string like "25.0"). */
  unitPrice: number | string;
  /** Minimum / maximum quantity the user may pick. */
  minQty?: number;
  maxQty?: number;
  /** Pack not purchasable (e.g. "Restocking") — the rail stays, locked, and
   *  the RIP button reads this label. */
  unavailableLabel?: string;
}

interface BarProps {
  quantity: number;
  minQty: number;
  maxQty: number;
  onQuantityChange: (next: number) => void;
  onBuy: () => void;
  priceLabel: string;
  disabled: boolean;
  /** Nothing to buy — `priceLabel` carries the "Sold out" / "Restocking"
   *  copy, the RIP button drops its quantity line and the stepper locks. */
  locked: boolean;
}

// Shell background is a 2-stop linear gradient. The direction lives in its
// own CSS var so the operator can flip horizontal/vertical from the
// VariantPicker without our React code observing the var. When both stops
// hold the same colour, the gradient renders as a solid fill — which is
// what classic uses by default (white).
const SHELL_BG =
  'linear-gradient(' +
  'var(--co-BottomActionShell-gradientDirection, to bottom), ' +
  'var(--co-BottomActionShell-gradientFrom, #ffffff), ' +
  'var(--co-BottomActionShell-gradientTo, #ffffff))';
const SHELL_BORDER = 'var(--co-BottomActionShell-border, rgba(171,174,179,0.24))';

// Host-app integration: embedded in an app that draws its own bottom
// navigation, the mobile rail ends up underneath it. Two Design
// Constructor knobs let the operator clear that UI without a code change:
//
//   marginBottom  — lifts the whole rail off the viewport bottom, leaving a
//                   transparent gap (host app's own background shows).
//   paddingBottom — keeps the rail glued to the bottom edge but extends the
//                   shell fill below the controls, so the host UI overlaps
//                   solid shell instead of the RIP button.
// `max(0px, …)` clamps a negative operator value to zero: the knob is a
// number input with spinner arrows, and a negative `bottom` would push the
// whole rail below the viewport (bar gone, no way to tell why).
const SHELL_MARGIN_BOTTOM = 'max(0px, var(--co-BottomActionShell-marginBottom, 0px))';
const SHELL_PADDING_BOTTOM = 'max(0px, var(--co-BottomActionShell-paddingBottom, 0px))';

// Mobile rail shadow — see MobileBar's docblock for why it is a
// `drop-shadow()` filter rather than a `box-shadow`. Knobs so a host app
// whose own bottom navigation sits right under the rail can soften or remove
// it (colour `transparent`) without a code change.
const SHELL_SHADOW =
  'drop-shadow(0 ' +
  'var(--co-BottomActionShell-shadowOffsetY, -6px) ' +
  'var(--co-BottomActionShell-shadowBlur, 20px) ' +
  'var(--co-BottomActionShell-shadowColor, rgba(0,0,0,0.18)))';

/** Height of the flat fill band below the controls — see the modern template's
 *  twin for the full reasoning. Short version: the rail sits ON the bottom edge
 *  and grows its fill DOWNWARD through the host's bottom inset, so the host's
 *  own navigation overlaps solid shell instead of a strip of page background.
 *  Plus whatever extra the operator dialled in. */
const SHELL_FILL_BELOW = `calc(var(--page-safe-bottom, 0px) + ${SHELL_PADDING_BOTTOM})`;

/** In-flow spacer height reserving room for the portal'd mobile rail: the
 *  rail's 160px visible band (the 201px SVG frame includes empty space on
 *  top) plus whatever bottom margin / padding the operator dialled in. The
 *  host inset is NOT added here — `#root` already carries it as
 *  `padding-bottom`, and the inset extends the rail downward rather than
 *  lifting it, so the rail's top edge does not move. Consumed by PackLayout. */
export const MOBILE_RAIL_SPACER_HEIGHT =
  `calc(160px + ${SHELL_MARGIN_BOTTOM} + ${SHELL_PADDING_BOTTOM})`;

/**
 * Mobile rail — edge-to-edge, fixed to viewport bottom.
 *
 * Shape = two overlapping white divs, each shaped by its own `clip-path`:
 *
 *   1. Wave (left/centre): a full-size white div clipped by a quadratic
 *      Bézier curve that SAGS in the middle (edges at y≈54.7%, centre
 *      drops to y≈61.7%, then rises back). Uses SVG `<clipPath>` because
 *      CSS `ellipse()` can only bulge outward — this curve is concave.
 *
 *   2. Plateau (right): a rectangle sized 69.6%×105.97% clipped by
 *      `inset(0 round 110px 0 0 0)` — rounded top-left corner only,
 *      matching Figma's `rounded-tl-[110px]`. Gradient white → #F4F4F4.
 *
 * `overflow-hidden` on the rail container clips anything that spills
 * past 0–100%. Shadow is a CSS `drop-shadow()` on the outer wrapper so
 * it follows the composite silhouette, not each piece separately.
 *
 * All coordinates are percentages of the 375×201 Figma frame, so the
 * shape scales naturally with rail width.
 */
function MobileBar({
  quantity,
  minQty,
  maxQty,
  onQuantityChange,
  onBuy,
  priceLabel,
  disabled,
  locked,
}: BarProps) {
  // useId so two mounted copies (mobile+desktop wrappers) don't collide.
  const clipId = `rail-wave-${useId().replace(/:/g, '')}`;

  // Render into <body> so the rail is guaranteed to be positioned against
  // the viewport (no ancestor `transform` / `filter` / `contain` can hijack
  // its containing block).
  //
  // The OUTER fixed div is its own `@container`: a `fixed left-0 right-0`
  // element spans the viewport EXCLUDING a classic scrollbar — the exact
  // width the pack layout's container queries measure. The INNER div hides
  // at `@[964px]` = the width where the desktop grid actually kicks in
  // (PackLayout's `@[900px]` content-box breakpoint + its own 2×32px
  // `@[900px]:px-8` padding). A viewport media query here would count the
  // scrollbar in and leave a scrollbar-wide window-width band (~964–979px)
  // with NO action bar at all.
  //
  // `z-[45]` — see the modern template's twin: the shell fills the host's bottom
  // inset itself, so it has to sit above Backdrop's bottom inset cover (z-40),
  // which paints the page background into that same strip. Still below dialogs
  // (z-50).
  return createPortal(
    <div
      className="@container fixed left-0 right-0 z-[45]"
      style={{ bottom: SHELL_MARGIN_BOTTOM }}
    >
      <div
        data-co-component="BottomActionShell"
        className="relative @[964px]:hidden"
        style={{ filter: SHELL_SHADOW }}
      >
        <div className="relative mx-auto h-[201px] w-full max-w-[1400px] overflow-hidden">
          {/* SVG defs — invisible, just hosts the wave clipPath */}
          <svg
            aria-hidden
            width="0"
            height="0"
            className="absolute"
            style={{ position: 'absolute' }}
          >
            <defs>
              <clipPath id={clipId} clipPathUnits="objectBoundingBox">
                {/* Quadratic Bézier sag: edges y=0.547, centre sags to y=0.617 */}
                <path d="M 0 0.547 Q 0.5 0.687 1 0.547 L 1 1 L 0 1 Z" />
              </clipPath>
            </defs>
          </svg>

          {/* 1. Wave — clipped by SVG <clipPath> (concave curve) */}
          <div
            aria-hidden
            className="absolute inset-0"
            style={{ clipPath: `url(#${clipId})`, background: SHELL_BG }}
          />

          {/* 2. Plateau — clipped by CSS inset with TL rounded corner */}
          <div
            aria-hidden
            className="absolute bg-gradient-to-b from-white to-[#F4F4F4]"
            style={{
              left: '57.07%',
              top: '31.34%',
              width: '69.6%',
              height: '105.97%',
              clipPath: 'inset(0 round 110px 0 0 0)',
            }}
          />

        </div>

        {/* Bottom padding band — flat shell fill continuing below the rail.
            The wave clip fills the rail edge-to-edge at its bottom edge, so
            this joins seamlessly and just makes the shell taller. 0px by
            default, i.e. no extra element height. */}
        <div
          aria-hidden
          className="mx-auto w-full max-w-[1400px]"
          style={{ height: SHELL_FILL_BELOW, background: SHELL_BG }}
        />

        {/* Controls sit OUTSIDE the shape container and AFTER the fill band —
            see the modern template's twin for the whole story. Short version:
            `overflow-hidden` up there is for the two clip-path pieces, and it
            was trimming the RIP disc's drop shadow with them (the shadow
            reaches 78px below the disc, the box ends 20px below it), while the
            opaque fill band painted over the remainder. Anchored to the band's
            top so the controls do not move and their shadows fall across it. */}
        <div
          className="absolute left-4"
          style={{ bottom: `calc(${SHELL_FILL_BELOW} + 50px)` }}
        >
          <QuantityStepper
            value={quantity}
            min={minQty}
            max={maxQty}
            onChange={onQuantityChange}
            size="md"
            disabled={locked}
          />
        </div>

        <div
          className="absolute right-[5px]"
          style={{ bottom: `calc(${SHELL_FILL_BELOW} + 20px)` }}
        >
          <RipButton
            onClick={onBuy}
            count={quantity}
            priceLabel={priceLabel}
            size="md"
            disabled={disabled}
            locked={locked}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Desktop rail — CSS-only, static, sits inside the sidebar column.
 * Uses the sidebar's own width; RIP button extends below the rail.
 */
function DesktopBar({
  quantity,
  minQty,
  maxQty,
  onQuantityChange,
  onBuy,
  priceLabel,
  disabled,
  locked,
}: BarProps) {
  return (
    <div
      data-co-component="BottomActionShell"
      className="hidden @[900px]:block relative min-h-[220px] w-full"
    >
      <div
        aria-hidden
        className="
          absolute inset-x-0 top-0 h-[127px]
          rounded-tl-[28px] rounded-tr-[28px]
          rounded-bl-[68px] rounded-br-[28px]
          [box-shadow:0_64px_34px_0_rgba(0,0,0,0.05)]
        "
        style={{
          background: SHELL_BG,
          border: `var(--co-BottomActionShell-borderWidth, 2px) solid ${SHELL_BORDER}`,
        }}
      />
      <div className="absolute left-[14px] top-[14px]">
        <QuantityStepper
          value={quantity}
          min={minQty}
          max={maxQty}
          onChange={onQuantityChange}
          size="lg"
          disabled={locked}
        />
      </div>
      {/* 900–1200px the sidebar is only 360px, so the RIP disc and the
          quantity stepper overlap. Shrink the disc (and its text) 10% toward
          the top-right corner so it pulls clear of the stepper; ≥1200px the
          sidebar widens to 400px and it sits at full size again. */}
      <div className="absolute right-[12px] top-[10px] origin-top-right @max-[1200px]:scale-90">
        <RipButton
          onClick={onBuy}
          count={quantity}
          priceLabel={priceLabel}
          size="lg"
          disabled={disabled}
          locked={locked}
        />
      </div>
    </div>
  );
}

/**
 * Pack-page bottom action.
 *
 *   mobile  — `fixed bottom-0`, edge-to-edge, SVG rail.
 *   desktop — `static`, CSS-only rail inside sidebar.
 *
 * Both bars hide/show via container queries measuring the SAME layout
 * width: the desktop bar's `@[900px]` resolves against PackLayout's
 * padded container (content-box hits 900 at a 964px-wide layout), and
 * the mobile bar's body portal carries its own `@container` on the fixed
 * wrapper with a matching `@[964px]:hidden` — so exactly one bar is
 * visible at any width. A viewport media query here would drift from the
 * containers by the scrollbar width.
 */
export function BottomAction({
  quantity,
  onQuantityChange,
  onBuy,
  unitPrice,
  minQty = 1,
  maxQty = 99,
  unavailableLabel,
}: BottomActionProps) {
  const unitAmount = Number(unitPrice) || 0;
  const totalAmount = unitAmount * Math.max(quantity, 1);
  const lockedLabel = unavailableLabel ?? (maxQty <= 0 ? 'Sold out' : null);
  const priceLabel = lockedLabel ?? formatPrice(totalAmount);

  const shared: BarProps = {
    quantity,
    minQty,
    maxQty,
    onQuantityChange,
    onBuy,
    priceLabel,
    disabled: lockedLabel !== null,
    locked: lockedLabel !== null,
  };

  return (
    <>
      <MobileBar {...shared} />
      <DesktopBar {...shared} />
    </>
  );
}
